/**
 * Employee management API (active tenant).
 *
 * Invite-only onboarding: administrators add employees; there is no self-registration into an existing
 * company. Backed by the BFF, which orchestrates Keycloak (Organization membership + portal roles) and
 * owns the guards — a `403` here can mean "you may not do this to yourself" or "the company would be left
 * without an owner", not only "you lack the permission".
 */
import { apiClient } from '@/shared/api';

export type EmployeeRole = 'OWNER' | 'ADMIN' | 'MANAGER' | 'STAFF';
export type EmployeeStatus = 'ACTIVE' | 'INVITED' | 'DISABLED';

/**
 * The roles an administrator may hand out — on an invitation and on a role change alike.
 *
 * <p>`OWNER` is absent by design: a company gets its owner at sign-up, and it moves only by transfer,
 * which is a different operation with a different guarantee (it demotes the outgoing owner in the same
 * step, so the company is never left with two owners or none).
 */
export const ASSIGNABLE_ROLES: EmployeeRole[] = ['ADMIN', 'MANAGER', 'STAFF'];

export interface Employee {
  id: string;
  name: string | null;
  email: string;
  /**
   * The portal role, or `null` when this person belongs to the company but holds none.
   *
   * Null is a real state, not a gap in the data: they can sign in and do nothing. It is deliberately not
   * defaulted to `STAFF` — that was defect D1, where a role-less account was shown as staff and implied
   * rights it did not have. Render the absence.
   */
  role: EmployeeRole | null;
  status: EmployeeStatus;
  /** When the account was created — the age of a still-pending invitation. */
  invitedAt?: string | null;
  /**
   * `false` when the account exists and has access, but its invitation email could not be delivered.
   *
   * They cannot sign in until it is resent, so this must be surfaced — an invitation that looks successful
   * while the person never hears about it is worse than a visible failure.
   */
  invitationEmailSent?: boolean | null;
}

export interface EmployeePage {
  items: Employee[];
  /** Total matching employees, or -1 when the identity provider could not supply a count. */
  total: number;
  page: number;
  size: number;
}

export interface EmployeeQuery {
  search?: string;
  status?: EmployeeStatus;
  page?: number;
  size?: number;
}

export interface InviteEmployeeRequest {
  email: string;
  role: EmployeeRole;
}

const BASE = '/api/v1/employees';

export const listEmployees = async (query: EmployeeQuery = {}): Promise<EmployeePage> =>
  (await apiClient.get<EmployeePage>(BASE, { params: query })).data;

export const getEmployee = async (id: string): Promise<Employee> =>
  (await apiClient.get<Employee>(`${BASE}/${encodeURIComponent(id)}/detail`)).data;

export const inviteEmployee = async (body: InviteEmployeeRequest): Promise<Employee> =>
  (await apiClient.post<Employee>(`${BASE}/invitations`, body)).data;

export const changeEmployeeRole = async (id: string, role: EmployeeRole): Promise<Employee> =>
  (await apiClient.put<Employee>(`${BASE}/${encodeURIComponent(id)}/role`, { role })).data;

/** Reversible: memberships and access grants survive, the person just cannot sign in. */
export const suspendEmployee = async (id: string): Promise<Employee> =>
  (await apiClient.post<Employee>(`${BASE}/${encodeURIComponent(id)}/suspend`)).data;

export const reactivateEmployee = async (id: string): Promise<Employee> =>
  (await apiClient.post<Employee>(`${BASE}/${encodeURIComponent(id)}/reactivate`)).data;

export const resendInvitation = async (id: string): Promise<Employee> =>
  (await apiClient.post<Employee>(`${BASE}/${encodeURIComponent(id)}/invitation/resend`)).data;

/** Invalidates a link already sent. Refused once the invitation has been accepted. */
export const revokeInvitation = async (id: string): Promise<void> => {
  await apiClient.delete(`${BASE}/${encodeURIComponent(id)}/invitation`);
};

/** Makes this employee the owner and demotes the caller to ADMIN. Only the current owner may call it. */
export const transferOwnership = async (id: string): Promise<Employee> =>
  (await apiClient.post<Employee>(`${BASE}/${encodeURIComponent(id)}/transfer-ownership`)).data;

/** Removes the employee from the company entirely. */
export const deactivateEmployee = async (id: string): Promise<void> => {
  await apiClient.delete(`${BASE}/${encodeURIComponent(id)}`);
};
